/*@flow*/
import type { Node } from 'react';
import React, { createElement, Fragment, createRef, useEffect } from 'react';
import * as t from 'lib/t';
import cx from 'classnames';
import Breakpoints from '../util/Breakpoints';
import LabelComponent from './Label';
import ErrorComponent from './ErrorComponent';
import Help from './Help';
import FormGroup from './FormGroup';
import type { LocalsT, TextboxLocals } from '../types';
import VerticalCols from './VerticalCols';
import { SectionBefore, SectionAfter } from './Sections';
import autosize from 'autosize';

type PropsT = LocalsT & TextboxLocals;

const TextboxConfig = t.struct(
  {
    addonBefore: t.Any,
    addonAfter: t.Any,
    horizontal: t.maybe(Breakpoints),
    vertical: t.maybe(Breakpoints),
    buttonBefore: t.Any,
    buttonAfter: t.Any,
  },
  'TextboxConfig'
);

function getAttrs(locals: PropsT) {
  const attrs = { ...locals.attrs };
  attrs.type = locals.type;
  attrs.className = cx(attrs.className);
  attrs.className += (attrs.className ? ' ' : '') + 'form-control';
  attrs.disabled = locals.disabled;
  if (locals.type !== 'file') {
    attrs.value = locals.value;
  }
  attrs.onChange =
    locals.type === 'file'
      ? (evt) => locals.onChange(evt.target.files[0])
      : (evt) => locals.onChange(evt.target.value);

  if (locals.help) {
    attrs['aria-describedby'] = attrs['aria-describedby'] || attrs.id + '-tip';
  }
  return attrs;
}

type Registry = $Exact<{
  HiddenTextbox: React$StatelessFunctionalComponent<*>,
  Static: React$StatelessFunctionalComponent<*>,
  Textbox: React$StatelessFunctionalComponent<*>,
  InputGroup: React$StatelessFunctionalComponent<*>,
  Input: React$StatelessFunctionalComponent<*>,
  Textarea: React$StatelessFunctionalComponent<*>,
  Label: React$StatelessFunctionalComponent<*>,
  Help: React$StatelessFunctionalComponent<*>,
  Vertical: React$StatelessFunctionalComponent<*>,
  Horizontal: React$StatelessFunctionalComponent<*>,
  FormGroup: React$StatelessFunctionalComponent<*>,
  getConfig(PropsT): any,
}>;

const clone = (overrides: $Shape<Registry> = {}) => {
  let registry = {};

  function getConfig(locals: PropsT) {
    return TextboxConfig(locals.config || {});
  }

  function HiddenTextbox(props: PropsT) {
    return <input type="hidden" value={props.value} name={props.attrs.name} />;
  }

  function Static(props: PropsT) {
    return <p className="form-control-plaintext">{props.value}</p>;
  }

  function Input(props: PropsT) {
    return <input {...props.attrs} />;
  }

  function Textarea(props: PropsT) {
    const myRef = createRef();
    useEffect(() => {
      const el = myRef.current;
      if (!el) return;
      autosize(el);
    }, [myRef]);
    return <textarea {...props.attrs} ref={myRef} />;
  }

  function InputField(props: PropsT) {
    if (props.type === 'static') return <registry.Static {...props} />;

    let input =
      props.type !== 'textarea' ? (
        <registry.Input {...props} />
      ) : (
        <registry.Textarea {...props} />
      );

    const { addonBefore, addonAfter, buttonBefore, buttonAfter } = props.config;
    if (addonBefore || addonAfter || buttonBefore || buttonAfter) {
      return <registry.InputGroup {...props} input={input} />;
    }
    return input;
  }

  function Addon({
    text,
    classSuffix = 'prepend',
  }: {
    text: string | React$Node,
    classSuffix?: string,
  }) {
    return (
      <span className={`input-group-${classSuffix}`}>
        <span className="input-group-text">{text}</span>
      </span>
    );
  }

  function InputGroup({ input, config }) {
    const { buttonBefore, addonBefore, addonAfter, buttonAfter } = config;
    return (
      <div className="input-group">
        {buttonBefore && <div className="input-group-btn">{buttonBefore}</div>}
        {addonBefore && <Addon text={addonBefore} />}
        {input}
        {addonAfter && <Addon text={addonAfter} classSuffix="append" />}
        {buttonAfter && <div className="input-group-btn">{buttonAfter}</div>}
      </div>
    );
  }

  function Label(locals: PropsT) {
    return createElement(LabelComponent, {
      label: locals.label,
      htmlFor: locals.attrs.id,
      breakpoints: locals.config.horizontal,
      className: cx({ 'text-danger font-weight-bold': locals.hasError }),
      isRequired: locals.isRequired,
    });
  }

  function Vertical(locals: PropsT) {
    return (
      <Fragment>
        <registry.Label {...locals} />
        <VerticalCols {...locals}>
          <InputField {...locals} />
          <registry.ErrorComponent {...locals} />
          <registry.Help {...locals} />
        </VerticalCols>
      </Fragment>
    );
  }

  function Horizontal(locals: PropsT) {
    const className = locals.label
      ? locals.config.horizontal.getInputClassName()
      : locals.config.horizontal.getOffsetClassName();

    return (
      <div className="form-row">
        <registry.Label {...locals} />
        <div className={cx(className)}>
          <InputField {...locals} />
          <registry.ErrorComponent {...locals} />
          <registry.Help {...locals} />
        </div>
      </div>
    );
  }

  function Textbox(props: PropsT) {
    const config = registry.getConfig(props);
    const attrs = getAttrs(props);
    const locals = { ...props, config, attrs };

    if (props.type === 'hidden') return <registry.HiddenTextbox {...locals} />;

    const children = config.horizontal ? (
      <registry.Horizontal {...locals} />
    ) : (
      <registry.Vertical {...locals} />
    );

    return (
      <Fragment>
        <registry.SectionBefore {...locals} />
        <registry.FormGroup {...locals}>{children}</registry.FormGroup>
        <registry.SectionAfter {...locals} />
      </Fragment>
    );
  }

  Textbox.clone = clone;

  registry = {
    ErrorComponent,
    HiddenTextbox,
    Static,
    Textbox,
    InputGroup,
    Input,
    Textarea,
    Label,
    Help,
    Vertical,
    Horizontal,
    FormGroup,
    getConfig,
    SectionBefore,
    SectionAfter,
    ...overrides,
  };

  return Textbox;
};

declare class CloneableClass {
  (locals: PropsT): Node;
  clone(overrides?: $Shape<Registry>): CloneableClass;
}
export default (clone(): CloneableClass);
